import { resolveCaseCitationsInHtml } from '@/domain/industrialCase/industrialCaseCitation';
import type { IndustrialCaseDocument } from '@/domain/industrialCase/industrialCaseDocument';
import 'ckeditor5/ckeditor5.css';

import * as S from './IndustrialCaseDetailPage.styles';
import { IndustrialCaseDocumentReferences } from './IndustrialCaseDocumentReferences';
import {
  IndustrialCaseContextSections,
  type IndustrialCaseFactSectionLabels,
  IndustrialCaseResultSections,
} from './IndustrialCaseFactSections';
import {
  IndustrialCasePublicationMeta,
  type IndustrialCasePublicationMetaLabels,
} from './IndustrialCasePublicationMeta';
import { IndustrialCaseRichText } from './IndustrialCaseRichText';
import { renderIndustrialCaseCitationMarker } from './renderIndustrialCaseCitationMarker';

/**
 * ### IndustrialCaseArticleBodyLabels
 *
 * Section headings, supplied by the caller so the live page can translate them
 * while the admin preview passes fixed Korean.
 */
export type IndustrialCaseArticleBodyLabels = IndustrialCaseFactSectionLabels &
  IndustrialCasePublicationMetaLabels & {
    readonly overview: string;
    readonly challenges: string;
    readonly outcomes: string;
    readonly references: string;
    readonly externalLink: string;
  };

/**
 * ### IndustrialCaseArticleBody
 *
 * Renders the readable part of a customer case: overview, field challenges, the
 * authored body sections, the closing metric, and the reference list.
 *
 * Shared deliberately between the live case page and the admin preview. The two
 * used to render from different shapes — the page from static data with
 * `paragraphs`, the preview from a document with `bodyHtml` — which is why the
 * preview could disagree with what visitors saw. Both now pass a document here.
 *
 * Page chrome that has nothing to do with the content (parallax hero, metric
 * sidebar, scroll progress, share actions, closing CTA) stays with each caller.
 *
 * The body is authored HTML from the rich-text editor and is injected as such,
 * the same trust level the site already applies to board content. Citation
 * markers are replaced here, with numbers derived from the reference order rather
 * than stored, so reordering references never leaves a stale number behind.
 */
export function IndustrialCaseArticleBody({
  document,
  labels,
}: {
  document: IndustrialCaseDocument;
  labels: IndustrialCaseArticleBodyLabels;
}) {
  const filledChallenges = document.challenges.filter((challenge) =>
    challenge.trim(),
  );

  return (
    <>
      {document.overview.trim() && (
        <section className={S.proseSection}>
          <h2 className={S.sectionHeading}>{labels.overview}</h2>
          <div className={S.prose}>
            <p>{document.overview}</p>
          </div>
        </section>
      )}

      {filledChallenges.length > 0 && (
        <section className={S.proseSection}>
          <h2 className={S.sectionHeading}>{labels.challenges}</h2>
          <div className={S.prose}>
            {filledChallenges.map((challenge) => (
              <p className={S.challengeParagraph} key={challenge}>
                {challenge}
              </p>
            ))}
          </div>
        </section>
      )}

      <IndustrialCaseContextSections document={document} labels={labels} />

      {document.narrative.map((section, index) => (
        <section className={S.proseSection} key={section.id}>
          <h2 className={S.sectionHeading}>
            {section.heading || `${labels.overview} ${index + 1}`}
          </h2>
          <IndustrialCaseRichText
            className={`${S.prose} industrial-case-prose ck-content [&_figcaption]:mt-3 [&_figcaption]:text-sm [&_figcaption]:text-[#74746f] [&_figure]:my-8 [&_img]:h-auto [&_img]:max-w-full`}
            html={resolveCaseCitationsInHtml(
              section.bodyHtml,
              document.references,
              renderIndustrialCaseCitationMarker,
            )}
          />
        </section>
      ))}

      {document.primaryMetric.value.trim() && (
        <section className={S.proseSection}>
          <h2 className={S.sectionHeading}>{labels.outcomes}</h2>
          <div className={S.closingMetric}>
            <strong className={S.closingMetricValue}>
              {document.primaryMetric.value}
            </strong>
            <span className={S.closingMetricLabel}>
              {document.primaryMetric.label}
            </span>
          </div>
        </section>
      )}

      <IndustrialCaseResultSections document={document} labels={labels} />

      <section className={S.proseSection}>
        <IndustrialCasePublicationMeta
          publishedAt={document.publishedAt}
          labels={labels}
        />
      </section>

      {document.references.length > 0 && (
        <section className={S.proseSection}>
          <IndustrialCaseDocumentReferences
            externalLinkLabel={labels.externalLink}
            references={document.references}
            title={labels.references}
          />
        </section>
      )}
    </>
  );
}
